/**
 * @fileoverview "Starting at Planning · Change": the stage the new plants from seed packets start at. A default that
 * is nearly always right, so one line until the gardener changes it; Change opens the stage list in place (Planning
 * first, "Not sure yet" last). Moved out of PacketReview for D78 (2026-10-22, rev 2 §3) so the "Check and save" packet
 * card has it too, the same line.
 *
 * Where: PacketReview, PacketPile.
 * API: none.
 * Words (en/es): packets.startingAt, packets.change, packets.stage, plants.form.fields.notSureYet.
 *
 * @module StartingStage
 */

import React from 'react';
import { Button } from '@/shared/components/ui/button';
import { NativeSelect } from '@/shared/components/ui/native-select';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { Stage } from '@/shared/types/stage';

/** What the line is given. */
interface StartingStageProps {
  /** The stages offered, Planning among them. */
  stages: readonly Stage[];
  /** The stage chosen, or null for "Not sure yet". */
  stageId: number | null;
  /** A stage chosen. */
  onChange: (stageId: number | null) => void;
  /** For the list's id. */
  id: string;
  /** True while the card or sheet is saving. */
  disabled?: boolean;
}

/**
 * The starting stage, as one line or, once Change is tapped, its list.
 *
 * @param props - StartingStageProps.
 */
export const StartingStage: React.FC<StartingStageProps> = ({ stages, stageId, onChange, id, disabled = false }) => {
  const { reactTranslate: t } = useTranslation();
  const [picking, setPicking] = React.useState(false);
  const stageNow = stageId === null ? t('plants.form.fields.notSureYet') : (stages.find((s) => s.stage_id === stageId)?.name ?? '');
  if (picking) {
    return (
      <div>
        <label htmlFor={id} className="mb-1 block text-xs text-muted-foreground">{t('packets.stage')}</label>
        <NativeSelect
          id={id}
          className="text-sm"
          value={stageId ?? ''}
          onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
          disabled={disabled}
        >
          {stages.map((stage) => <option key={stage.stage_id} value={stage.stage_id}>{stage.name}</option>)}
          <option value="">{t('plants.form.fields.notSureYet')}</option>
        </NativeSelect>
      </div>
    );
  }
  return (
    <p className="flex flex-wrap items-center text-sm text-muted-foreground">
      {t('packets.startingAt', { stage: stageNow })}
      <Button type="button" variant="ghost" size="sm" className="h-11 px-2 text-primary" onClick={() => setPicking(true)} disabled={disabled}>
        {t('packets.change')}
      </Button>
    </p>
  );
};
